import type { GroupListDTO } from "@/generated-client";
import type { Localizer } from "@/main/react/Types";

/**
 * The member's group lists (SV-144), as the client holds them.
 *
 * Favorites is a built-in list (A3): `listKey` is the literal `favorites`, `name` is null and the label
 * is `groups.lists.favorites`. The server synthesizes it (empty) until the first star, and lists it
 * first; custom lists follow in their own `position` order. Pure functions only: this module is the
 * testable half of `groupListsQuery.ts`.
 */
export type GroupList = {
    listKey: string;
    kind: "FAVORITES" | "CUSTOM";
    name: string | null;
    groupPublicIds: string[];
};

export const FAVORITES_KEY = "favorites";

/** The maximum custom lists (D5). Favorites is never counted. The server refuses the 51st with a 400. */
export const MAX_CUSTOM_LISTS = 50;

/** Name bounds (D5 / A7). The server normalizes and is the authority; this only gates the Save button. */
export const LIST_NAME_MAX_CODE_POINTS = 40;

export function normalizeGroupList(dto: GroupListDTO | null | undefined): GroupList {
    const isFavorites = dto?.kind === "FAVORITES" || dto?.listKey === FAVORITES_KEY;
    return {
        listKey: isFavorites ? FAVORITES_KEY : (dto?.listKey ?? ""),
        kind: isFavorites ? "FAVORITES" : "CUSTOM",
        name: isFavorites ? null : (dto?.name ?? ""),
        groupPublicIds: Array.isArray(dto?.groupPublicIds) ? dto.groupPublicIds.filter(Boolean) : [],
    };
}

/** Favorites first (synthesized when absent), then custom lists in the order the server sent them. */
export function normalizeGroupLists(dtos: readonly (GroupListDTO | null | undefined)[] | null | undefined): GroupList[] {
    const lists = (dtos ?? []).map(normalizeGroupList).filter((list) => list.listKey !== "");
    const favorites = lists.find((list) => list.kind === "FAVORITES")
        ?? { listKey: FAVORITES_KEY, kind: "FAVORITES" as const, name: null, groupPublicIds: [] };
    return [favorites, ...lists.filter((list) => list.kind !== "FAVORITES")];
}

export function listsEqual(left: readonly GroupList[], right: readonly GroupList[]): boolean {
    return left === right || (left.length === right.length && left.every((list, index) => listEqual(list, right[index])));
}

function listEqual(left: GroupList, right: GroupList): boolean {
    return left === right || (
        left.listKey === right.listKey
        && left.kind === right.kind
        && left.name === right.name
        && left.groupPublicIds.length === right.groupPublicIds.length
        && left.groupPublicIds.every((id, index) => id === right.groupPublicIds[index])
    );
}

/**
 * Replace (or append) one list with the server's returned value. Returns `lists` itself when nothing
 * changed, so a write that changes nothing notifies nobody (rule 31).
 */
export function upsertList(lists: readonly GroupList[], updated: GroupList): GroupList[] {
    const index = lists.findIndex((list) => list.listKey === updated.listKey);
    if (index >= 0 && listEqual(lists[index], updated)) {
        return lists as GroupList[];
    }
    const next = index >= 0
        ? lists.map((list, position) => position === index ? updated : list)
        : [...lists, updated];
    return next;
}

export function removeListFrom(lists: readonly GroupList[], listKey: string): GroupList[] {
    return lists.some((list) => list.listKey === listKey)
        ? lists.filter((list) => list.listKey !== listKey)
        : lists as GroupList[];
}

export function favoritesOf(lists: readonly GroupList[]): GroupList | null {
    return lists.find((list) => list.kind === "FAVORITES") ?? null;
}

export function customListsOf(lists: readonly GroupList[]): GroupList[] {
    return lists.filter((list) => list.kind === "CUSTOM");
}

export function isFavoriteGroup(lists: readonly GroupList[], groupPublicId: string): boolean {
    return Boolean(favoritesOf(lists)?.groupPublicIds.includes(groupPublicId));
}

export function listLabel(list: GroupList, localizer: Localizer): string {
    return list.kind === "FAVORITES" ? localizer("groups.lists.favorites") : (list.name ?? "");
}

/** The manual order after moving one group by `delta` places (D7's Move up / Move down twin). */
export function moveGroupInOrder(order: readonly string[], groupPublicId: string, delta: -1 | 1): string[] {
    const from = order.indexOf(groupPublicId);
    const to = from + delta;
    if (from < 0 || to < 0 || to >= order.length) {
        return order as string[];
    }
    const next = [...order];
    next.splice(from, 1);
    next.splice(to, 0, groupPublicId);
    return next;
}

/** A7 mirrored for the Save button only: trimmed, whitespace collapsed, 1 to 40 code points. */
export function normalizeListName(raw: string): string {
    return raw.replace(/\s+/g, " ").trim();
}

export function isValidListName(raw: string): boolean {
    const name = normalizeListName(raw);
    // eslint-disable-next-line no-control-regex
    return name.length > 0 && Array.from(name).length <= LIST_NAME_MAX_CODE_POINTS && !/[\u0000-\u001f\u007f]/.test(name);
}
